import Card from '@/components/elements/Card/Card';
import { ListItem } from '@/components/elements/lists';
import Snackbar from '@/components/elements/notifications/Snackbar/Snackbar';
import Icon from '@/components/icons/Icon';
import { _s } from '@/locale';
import { PLACE_IMPRESSION, placeService } from '@/services';
import type { ButtonID } from '@bokadirekt/tracking-schema/marketplace';
import { useState } from 'react';
import { toast } from 'react-toastify';

const baseTranslationKey = 'features.ProfilePages.components.Links';

type LinksProps = {
  instagram?: { name?: string; link?: string };
  facebook?: { name?: string; link?: string };
  website?: string;
  onWebsiteClick: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  onButtonClick?: (buttonId: ButtonID) => void;
  placeId: number;
};

type CopiedType = 'web' | 'ig' | 'fb' | null;

const Links = ({ placeId, instagram, facebook, website, onWebsiteClick, onButtonClick }: LinksProps) => {
  const [copied, setCopied] = useState<CopiedType>(null);

  const handleCopyText = (text: string, type: CopiedType) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => {
      setCopied(null);
    }, 2000);
    toast(({ closeToast }) => (
      <Snackbar type="success" label={_s(`${baseTranslationKey}.copy`)} onClose={closeToast} />
    ));
  };

  const handleWebsiteClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    onWebsiteClick(event);
    onButtonClick?.('place_details_website');
  };

  const handleInstagramClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    onButtonClick?.('place_details_instagram');
    if (instagram.name) {
      placeService.storePlaceImpressions([placeId], PLACE_IMPRESSION.PLACE_INSTAGRAM_CLICK);
    }
  };

  const handleFacebookClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    onButtonClick?.('place_details_facebook');
    if (facebook.name) {
      placeService.storePlaceImpressions([placeId], PLACE_IMPRESSION.PLACE_FACEBOOK_CLICK);
    }
  };

  return (
    <Card size="lg">
      <div className="gap-xs flex flex-col">
        <h2 className="text-h-s">{_s(`${baseTranslationKey}.title`)}</h2>
        <ul>
          {website && (
            <ListItem
              leftPadding={false}
              className="gap-sm"
              underline
              leftSlot={<Icon variant="website" color="fg_secondary" />}
              {...(website
                ? {
                    rightSlot: (
                      <button
                        className="flex outline-none"
                        aria-label={_s(`${baseTranslationKey}.ariaLabels.copyWeb`)}
                        onClick={() => handleCopyText(website, 'web')}>
                        <Icon
                          variant={copied === 'web' ? 'copy-filled' : 'copy'}
                          color="fg_primary"
                          style={{ height: '20px', width: '20px' }}
                        />
                      </button>
                    ),
                  }
                : {})}>
              <a
                href={website}
                target="_blank"
                rel="noreferrer"
                onClick={handleWebsiteClick}
                className="text-l text-fg_link underline">
                {website.replace(/^https?:\/\//, '')}
              </a>
            </ListItem>
          )}
          {instagram.name && (
            <ListItem
              leftPadding={false}
              className="gap-sm"
              underline
              leftSlot={<Icon variant="instagram" color="fg_secondary" />}
              {...(instagram.name
                ? {
                    rightSlot: (
                      <button
                        className="flex outline-none"
                        aria-label={_s(`${baseTranslationKey}.ariaLabels.copyInstagram`)}
                        onClick={() => handleCopyText(instagram.link, 'ig')}>
                        <Icon
                          variant={copied === 'ig' ? 'copy-filled' : 'copy'}
                          color="fg_primary"
                          style={{ height: '20px', width: '20px' }}
                        />
                      </button>
                    ),
                  }
                : {})}>
              <a
                href={instagram.link}
                target="_blank"
                rel="noreferrer"
                onClick={handleInstagramClick}
                className="text-l text-fg_link underline">
                {instagram.name ?? _s(`${baseTranslationKey}.addInstagram`)}
              </a>
            </ListItem>
          )}
          {facebook.name && (
            <ListItem
              leftPadding={false}
              className="gap-sm"
              leftSlot={<Icon variant="facebook" color="fg_secondary" />}
              {...(facebook.name
                ? {
                    rightSlot: (
                      <button
                        className="flex outline-none"
                        aria-label={_s(`${baseTranslationKey}.ariaLabels.copyFacebook`)}
                        onClick={() => handleCopyText(facebook.link, 'fb')}>
                        <Icon
                          variant={copied === 'fb' ? 'copy-filled' : 'copy'}
                          color="fg_primary"
                          style={{ height: '20px', width: '20px' }}
                        />
                      </button>
                    ),
                  }
                : {})}>
              <a
                href={facebook.link}
                onClick={handleFacebookClick}
                target="_blank"
                rel="noreferrer"
                className="text-l text-fg_link underline">
                {facebook.name ?? _s(`${baseTranslationKey}.addFacebook`)}
              </a>
            </ListItem>
          )}
        </ul>
      </div>
    </Card>
  );
};

export default Links;
